import ListItem from '@mui/material/ListItem';
import type { NoteComment } from '@witmetrics/utils';
import { useDispatch, usePracticeUsers } from '@witmetrics/data-store';
import { mapToKey } from '@witmetrics/utils/arrays';
import { useToggle } from '@/hooks/useToggle';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import NotesField from '@/components/inputs/NotesField';
import Note from '@/components/Note';
import BlockHeader from '../../BlockHeader';
import { classes, parseContent, styles, updateNoteComment } from './utils';

export type CommentProps = {
  editable?: boolean;
  comment: NoteComment;
};

export default function Comment({ editable, comment }: CommentProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const practiceUsers = usePracticeUsers();
  const [isEditing, toggleEditing] = useToggle();
  const [isSaving, toggleSaving] = useToggle();
  const [isDeleting, toggleDeleting] = useToggle();

  const handleSave = async (message: string) => {
    try {
      toggleSaving(true);
      dispatch.addNoteComments([await updateNoteComment(comment.id, message)]);
      toggleSaving(false);
      toggleEditing(false);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving comment');
    }
  };

  const handleDelete = async () => {
    try {
      toggleDeleting(true);
      await API.Notes.removeNoteComment(comment.id);
      dispatch.deleteNoteComment({ noteCommentId: comment.id });
      toggleDeleting(false);
    } catch (err) {
      toggleDeleting(false);
      onApiError(err, 'Error deleting comment');
    }
  };

  const renderContent = () => {
    const content = parseContent(comment);
    return isEditing ? (
      <NotesField
        isSaving={isSaving}
        className={classes.notesField}
        placeholder="New comment..."
        initialMessage={content}
        users={mapToKey(practiceUsers, 'user')}
        onSave={handleSave}
        onCancel={() => toggleEditing()}
      />
    ) : (
      <Note className={classes.note} value={content} />
    );
  };

  return (
    <ListItem key={comment.id} sx={styles.comment} className={classes.comment}>
      <BlockHeader
        editable={editable}
        createdBy={comment.createdBy}
        createdAt={comment.createdAt}
        onEdit={() => toggleEditing(true)}
        onDelete={handleDelete}
      />
      {renderContent()}
    </ListItem>
  );
}
